import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtDate, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import WhatsAppShareButton from "@/components/crm/WhatsAppShareButton";

export default function TaxInvoicePrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [co, setCo] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  useEffect(() => {
    api.get(`/tax-invoices/${id}`).then(({ data }) => setT(data));
    api.get("/settings/company").then(({ data }) => setCo(data)).catch(() => {});
  }, [id]);

  if (!t) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  // Firm snapshot takes priority over company settings
  const firm = t.firm_snapshot || co || {};
  const items = t.items || [];
  const theme = t.theme || "tally";
  const asset = (p) => p ? (p.startsWith("data:") || p.startsWith("http") || p.startsWith("/") ? p : `${API}/files/${p}?auth=${token}`) : null;

  return (
    <div className="min-h-screen bg-slate-100 py-6 tally-print">
      <div className="no-print max-w-4xl mx-auto flex justify-between items-center mb-4 px-4">
        <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-1 font-semibold">
            Theme: <span className="text-amber-700 uppercase">{theme}</span>
          </div>
          <WhatsAppShareButton
            mobile={t.customer_mobile}
            message={`Namaste ${t.customer_name || ""},\n\nYeh raha aapka Tax Invoice — ${t.invoice_no}.\n\nTaxable: ${fmtINR(t.taxable_amount)}\nCGST: ${fmtINR(t.cgst_amount)}\nSGST: ${fmtINR(t.sgst_amount)}\nGrand Total: ${fmtINR(t.grand_total)}\n\nAmount in words: ${t.amount_in_words || ""}\n\n— ${firm.name || firm.company_name || "MRM Renewables"}`}
            label="Share on WhatsApp"
            variant="solid"
            size="default"
            testid="whatsapp-tax-invoice-print"
          />
          <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.print()} data-testid="print-tax-invoice-btn"><Printer className="w-4 h-4 mr-1" />Print / Save PDF</Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto shadow-lg print-page" data-testid="tax-invoice-doc">
        {theme === "modern" && <ModernTheme t={t} firm={firm} items={items} asset={asset}/>}
        {theme === "corporate" && <CorporateTheme t={t} firm={firm} items={items} asset={asset}/>}
        {(theme === "tally" || (theme!=="modern" && theme!=="corporate")) && <TallyTheme t={t} firm={firm} items={items} asset={asset}/>}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// THEME 1 — TALLY CLASSIC (traditional bordered black & white)
// ────────────────────────────────────────────────────────────────
function TallyTheme({ t, firm, items, asset }) {
  return (
    <div className="bg-white" style={{fontFamily:'"Roboto Mono","Courier New",monospace', fontSize: 12}}>
      <div className="border-2 border-black">
        <div className="text-center border-b-2 border-black py-1 text-sm font-bold tracking-widest bg-slate-100">TAX INVOICE</div>
        <div className="grid grid-cols-12 border-b-2 border-black">
          <div className="col-span-8 border-r-2 border-black p-3">
            <div className="text-base font-extrabold uppercase tracking-tight" style={{fontFamily:'Outfit'}}>{firm.name || firm.company_name || "MRM RENEWABLES"}</div>
            {firm.address && <div className="text-[11px] leading-snug mt-0.5">{firm.address}</div>}
            <div className="text-[11px] mt-0.5">Tel: {firm.phone || "-"} · Email: {firm.email || "-"}</div>
            <div className="text-[11px] font-semibold mt-1">GSTIN/UIN: <span className="tracking-wide">{firm.gstin || "-"}</span></div>
            <div className="text-[11px]">State Name: {firm.state || "Rajasthan, Code: 08"}</div>
            {firm.cin && <div className="text-[11px]">CIN: {firm.cin}</div>}
          </div>
          <div className="col-span-4 grid grid-cols-2">
            <Cell label="Invoice No." value={t.invoice_no} bold/>
            <Cell label="Dated" value={fmtDate(t.invoice_date || t.created_at)}/>
            <Cell label="Delivery Note" value="-"/>
            <Cell label="Mode/Terms of Payment" value="On Delivery"/>
            <Cell label="Reference No. & Date" value={t.quotation_id ? `Ref: ${t.quotation_id.slice(0,8)}` : "-"}/>
            <Cell label="Other References" value="-"/>
          </div>
        </div>
        <div className="grid grid-cols-12 border-b-2 border-black">
          <div className="col-span-8 border-r-2 border-black p-3">
            <div className="text-[10px] uppercase tracking-wider mb-1 font-semibold">Buyer (Bill To)</div>
            <div className="font-bold text-sm">{t.customer_name}</div>
            {t.customer_address && <div className="text-[11px]">{t.customer_address}</div>}
            {t.customer_city && <div className="text-[11px]">{t.customer_city}</div>}
            <div className="text-[11px]">Mob: {t.customer_mobile}</div>
            <div className="text-[11px] mt-1">Place of Supply: <span className="font-semibold">{t.place_of_supply || "Rajasthan (08)"}</span></div>
          </div>
          <div className="col-span-4 grid grid-cols-2">
            <Cell label="Buyer's Order No." value="-"/><Cell label="Dated" value="-"/>
            <Cell label="Dispatch Doc No." value="-"/><Cell label="Delivery Note Date" value="-"/>
            <Cell label="Dispatched through" value="Company Vehicle"/><Cell label="Destination" value={t.customer_city || "-"}/>
          </div>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-black bg-slate-50 text-[11px]">
              <th className="border-r border-black py-1 px-1 w-10">Sl.</th>
              <th className="border-r border-black py-1 px-2 text-left">Description of Goods / Services</th>
              <th className="border-r border-black py-1 px-1 w-20">HSN/SAC</th>
              <th className="border-r border-black py-1 px-1 w-16">Qty</th>
              <th className="border-r border-black py-1 px-1 w-14">Unit</th>
              <th className="border-r border-black py-1 px-1 w-24 text-right">Rate</th>
              <th className="py-1 px-1 w-28 text-right">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it,i)=>(
              <tr key={i} className="text-[11px] align-top">
                <td className="border-r border-black px-1 py-1.5 text-center">{i+1}</td>
                <td className="border-r border-black px-2 py-1.5">{it.description}</td>
                <td className="border-r border-black px-1 py-1.5 text-center">{it.hsn}</td>
                <td className="border-r border-black px-1 py-1.5 text-right">{it.qty}</td>
                <td className="border-r border-black px-1 py-1.5 text-center">{it.unit}</td>
                <td className="border-r border-black px-1 py-1.5 text-right font-mono">{fmtINR(it.rate)}</td>
                <td className="px-1 py-1.5 text-right font-mono">{fmtINR(it.amount || it.qty*it.rate)}</td>
              </tr>
            ))}
            {Array.from({length: Math.max(0, 6-items.length)}).map((_,i)=>(
              <tr key={`f${i}`} className="text-[11px]"><td className="border-r border-black py-1.5">&nbsp;</td><td className="border-r border-black"></td><td className="border-r border-black"></td><td className="border-r border-black"></td><td className="border-r border-black"></td><td className="border-r border-black"></td><td></td></tr>
            ))}
            <tr className="border-t-2 border-black text-[11px]"><td colSpan={6} className="border-r border-black text-right px-2 py-1 font-semibold">Sub Total</td><td className="px-1 py-1 text-right font-mono font-semibold">{fmtINR(t.taxable_amount)}</td></tr>
            {(t.cgst_amount||0) > 0 && <tr className="text-[11px]"><td colSpan={6} className="border-r border-black text-right px-2 py-1">CGST @ {t.cgst_percent}%</td><td className="px-1 py-1 text-right font-mono">{fmtINR(t.cgst_amount)}</td></tr>}
            {(t.sgst_amount||0) > 0 && <tr className="text-[11px]"><td colSpan={6} className="border-r border-black text-right px-2 py-1">SGST @ {t.sgst_percent}%</td><td className="px-1 py-1 text-right font-mono">{fmtINR(t.sgst_amount)}</td></tr>}
            {(t.igst_amount||0) > 0 && <tr className="text-[11px]"><td colSpan={6} className="border-r border-black text-right px-2 py-1">IGST @ {t.igst_percent}%</td><td className="px-1 py-1 text-right font-mono">{fmtINR(t.igst_amount)}</td></tr>}
            <tr className="border-t-2 border-black bg-slate-100 text-sm font-bold"><td colSpan={6} className="border-r border-black text-right px-2 py-2">Grand Total</td><td className="px-1 py-2 text-right font-mono">₹ {fmtINR(t.grand_total).replace("₹","")}</td></tr>
          </tbody>
        </table>
        <div className="border-t-2 border-black p-2 text-[11px]"><span className="font-semibold">Amount Chargeable (in words):</span> <span className="italic">{t.amount_in_words || "-"}</span></div>
        <div className="grid grid-cols-2 border-t-2 border-black">
          <div className="border-r-2 border-black p-3 text-[11px]">
            <div className="font-semibold mb-1">Company's PAN: <span className="font-mono">{firm.pan || "-"}</span></div>
            <div className="font-semibold mt-2 mb-1">Declaration</div>
            <p className="leading-snug">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</p>
            {(firm.bank_name || firm.account_no) && (
              <div className="mt-2">
                <div className="font-semibold">Company's Bank Details</div>
                <div>Bank: {firm.bank_name || "-"}</div>
                <div>A/c No.: <span className="font-mono">{firm.account_no||"-"}</span></div>
                <div>IFS: <span className="font-mono">{firm.ifsc||"-"}</span></div>
              </div>
            )}
          </div>
          <div className="p-3 text-[11px]">
            <div className="text-right">
              <div className="mb-2">for <span className="font-semibold">{firm.name || firm.company_name || "MRM Renewables"}</span></div>
              {t.created_by_signature && <img src={asset(t.created_by_signature)} alt="Signature" className="h-14 object-contain ml-auto mb-1" data-testid="tax-invoice-sales-signature"/>}
              <div className="border-t border-black inline-block px-6 pt-1 font-semibold" data-testid="tax-invoice-sales-name">{t.created_by_name || "Authorised Signatory"}</div>
              {t.created_by_name && <div className="text-[9px] text-slate-500 mt-0.5">Sales Representative</div>}
            </div>
            <div className="text-center mt-4 pt-3 border-t border-slate-300 text-[10px] text-slate-500">This is a Computer Generated Invoice</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// THEME 2 — MODERN AMBER (clean colored header, soft rows)
// ────────────────────────────────────────────────────────────────
function ModernTheme({ t, firm, items, asset }) {
  return (
    <div className="bg-white" style={{fontFamily:'Outfit'}}>
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          {firm.logo_url && <img src={asset(firm.logo_url)} alt="" className="h-14 object-contain bg-white rounded-lg p-1"/>}
          <div>
            <div className="text-2xl font-extrabold uppercase tracking-tight leading-tight">{firm.name || firm.company_name || "MRM Renewables"}</div>
            <div className="text-amber-100 text-xs mt-1">{firm.address}</div>
            <div className="text-amber-100 text-xs">GSTIN: {firm.gstin || "-"} · {firm.phone} · {firm.email}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest opacity-90">Tax Invoice</div>
          <div className="text-2xl font-black">{t.invoice_no}</div>
          <div className="text-xs opacity-90">{fmtDate(t.invoice_date || t.created_at)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 p-6 border-b border-slate-200">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Bill To</div>
          <div className="text-lg font-bold text-slate-900">{t.customer_name}</div>
          <div className="text-sm text-slate-600">{t.customer_address}</div>
          <div className="text-sm text-slate-600">{t.customer_city}</div>
          <div className="text-sm text-slate-600">Mob: {t.customer_mobile}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Place of Supply</div>
          <div className="text-sm font-semibold text-slate-900">{t.place_of_supply}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-3 mb-1">Payment Terms</div>
          <div className="text-sm text-slate-700">On Delivery</div>
        </div>
      </div>

      <div className="p-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-amber-500 text-slate-600 text-xs uppercase">
              <th className="text-left py-2 pr-2 w-10">#</th>
              <th className="text-left py-2 pr-2">Description</th>
              <th className="text-center py-2 px-2 w-24">HSN</th>
              <th className="text-right py-2 px-2 w-16">Qty</th>
              <th className="text-right py-2 pl-2 w-28">Rate</th>
              <th className="text-right py-2 pl-2 w-32">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it,i)=>(
              <tr key={i} className={i%2 ? "bg-slate-50" : ""}>
                <td className="py-3 pr-2 font-mono text-slate-500">{String(i+1).padStart(2,'0')}</td>
                <td className="py-3 pr-2 font-medium">{it.description}<div className="text-xs text-slate-500">{it.unit}</div></td>
                <td className="py-3 px-2 text-center font-mono text-xs">{it.hsn}</td>
                <td className="py-3 px-2 text-right font-mono">{it.qty}</td>
                <td className="py-3 pl-2 text-right font-mono">{fmtINR(it.rate)}</td>
                <td className="py-3 pl-2 text-right font-mono font-semibold">{fmtINR(it.amount || it.qty*it.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 pb-6">
        <div className="flex justify-end">
          <div className="w-80 space-y-1 text-sm">
            <Row label="Taxable Amount" value={fmtINR(t.taxable_amount)}/>
            {(t.cgst_amount||0)>0 && <Row label={`CGST @ ${t.cgst_percent}%`} value={fmtINR(t.cgst_amount)}/>}
            {(t.sgst_amount||0)>0 && <Row label={`SGST @ ${t.sgst_percent}%`} value={fmtINR(t.sgst_amount)}/>}
            {(t.igst_amount||0)>0 && <Row label={`IGST @ ${t.igst_percent}%`} value={fmtINR(t.igst_amount)}/>}
            {(t.round_off||0)!==0 && <Row label="Round Off" value={fmtINR(t.round_off)}/>}
            <div className="flex justify-between items-center bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg px-4 py-3 mt-2">
              <div className="text-xs uppercase tracking-widest">Grand Total</div>
              <div className="text-xl font-black font-mono">{fmtINR(t.grand_total)}</div>
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-600 italic border-t pt-3">In words: <span className="font-semibold">{t.amount_in_words}</span></div>
      </div>

      <div className="grid grid-cols-2 gap-6 p-6 border-t border-slate-200">
        <div className="text-xs">
          <div className="font-bold text-slate-700 mb-1">Bank Details</div>
          <div>Bank: {firm.bank_name || "-"}</div>
          <div>A/c No.: <span className="font-mono">{firm.account_no||"-"}</span></div>
          <div>IFSC: <span className="font-mono">{firm.ifsc||"-"}</span></div>
          <div className="font-bold text-slate-700 mt-3 mb-1">PAN</div>
          <div className="font-mono">{firm.pan || "-"}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">for <span className="font-semibold text-slate-900">{firm.name || firm.company_name}</span></div>
          {t.created_by_signature && <img src={asset(t.created_by_signature)} alt="" className="h-14 object-contain ml-auto my-1"/>}
          <div className="border-t border-slate-300 inline-block px-6 pt-1 text-sm font-semibold">{t.created_by_name || "Authorised Signatory"}</div>
          <div className="text-[10px] text-slate-500">Sales Representative</div>
        </div>
      </div>

      <div className="bg-slate-50 text-center text-[10px] text-slate-500 py-2 border-t border-slate-200">This is a computer generated invoice · Powered by MRM CRM</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// THEME 3 — CORPORATE ELEGANT (slate & gold, executive tone)
// ────────────────────────────────────────────────────────────────
function CorporateTheme({ t, firm, items, asset }) {
  return (
    <div className="bg-white" style={{fontFamily:'"Helvetica Neue","Segoe UI",sans-serif'}}>
      <div className="bg-slate-900 text-white p-8 flex items-start justify-between relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500 rounded-full opacity-20 -translate-y-1/2 translate-x-1/2"/>
        <div className="relative">
          {firm.logo_url && <img src={asset(firm.logo_url)} alt="" className="h-12 object-contain mb-3 bg-white/10 rounded p-1"/>}
          <div className="text-xl font-bold tracking-wide" style={{fontFamily:'Outfit'}}>{firm.name || firm.company_name || "MRM Renewables"}</div>
          <div className="text-slate-300 text-[11px] mt-1 max-w-md">{firm.address}</div>
          <div className="text-slate-300 text-[11px]">GSTIN: {firm.gstin || "-"} · PAN: {firm.pan || "-"}</div>
        </div>
        <div className="text-right relative">
          <div className="text-amber-400 text-[10px] uppercase tracking-[0.3em] font-semibold">Tax Invoice</div>
          <div className="text-3xl font-black tracking-tight mt-1">{t.invoice_no}</div>
          <div className="text-slate-400 text-xs mt-1">Dated {fmtDate(t.invoice_date || t.created_at)}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 px-8 py-5 bg-slate-50 border-b border-slate-200 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Billed To</div>
          <div className="font-bold text-slate-900 text-sm">{t.customer_name}</div>
          <div className="text-slate-600">{t.customer_address}</div>
          <div className="text-slate-600">{t.customer_city}</div>
          <div className="text-slate-600">{t.customer_mobile}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Contact</div>
          <div className="text-slate-800">{firm.phone || "-"}</div>
          <div className="text-slate-800">{firm.email || "-"}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Supply / State</div>
          <div className="text-slate-800">{t.place_of_supply}</div>
          <div className="text-slate-800">{firm.state || "Rajasthan (08)"}</div>
        </div>
      </div>

      <div className="p-8">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b-2 border-slate-900 text-[9px] uppercase tracking-widest text-slate-500 font-bold">
              <th className="text-left py-2 pr-2 w-8">Sl</th>
              <th className="text-left py-2 pr-2">Description</th>
              <th className="text-center py-2 px-2 w-20">HSN</th>
              <th className="text-right py-2 px-2 w-14">Qty</th>
              <th className="text-right py-2 pl-2 w-24">Rate</th>
              <th className="text-right py-2 pl-2 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it,i)=>(
              <tr key={i} className="border-b border-slate-100">
                <td className="py-3 pr-2 font-mono text-slate-500">{i+1}</td>
                <td className="py-3 pr-2"><div className="font-semibold text-slate-900">{it.description}</div><div className="text-slate-500">{it.unit}</div></td>
                <td className="py-3 px-2 text-center font-mono">{it.hsn}</td>
                <td className="py-3 px-2 text-right font-mono">{it.qty}</td>
                <td className="py-3 pl-2 text-right font-mono">{fmtINR(it.rate)}</td>
                <td className="py-3 pl-2 text-right font-mono font-semibold">{fmtINR(it.amount || it.qty*it.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mt-4">
          <div className="w-96">
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <Row2 label="Taxable Amount" value={fmtINR(t.taxable_amount)}/>
              {(t.cgst_amount||0)>0 && <Row2 label={`CGST @ ${t.cgst_percent}%`} value={fmtINR(t.cgst_amount)}/>}
              {(t.sgst_amount||0)>0 && <Row2 label={`SGST @ ${t.sgst_percent}%`} value={fmtINR(t.sgst_amount)}/>}
              {(t.igst_amount||0)>0 && <Row2 label={`IGST @ ${t.igst_percent}%`} value={fmtINR(t.igst_amount)}/>}
              {(t.round_off||0)!==0 && <Row2 label="Round Off" value={fmtINR(t.round_off)}/>}
              <div className="flex justify-between px-4 py-3 bg-slate-900 text-white">
                <div className="text-xs uppercase tracking-widest text-amber-400 font-bold">Grand Total</div>
                <div className="text-lg font-black font-mono">{fmtINR(t.grand_total)}</div>
              </div>
            </div>
            <div className="mt-2 text-[10px] italic text-slate-500">In words: {t.amount_in_words}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 px-8 pb-8 pt-4 border-t border-slate-200 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Bank Details</div>
          <div>{firm.bank_name || "-"}</div>
          <div className="font-mono">A/c: {firm.account_no || "-"}</div>
          <div className="font-mono">IFSC: {firm.ifsc || "-"}</div>
        </div>
        <div className="text-right">
          <div className="text-slate-500">for</div>
          <div className="font-semibold text-slate-900">{firm.name || firm.company_name}</div>
          {t.created_by_signature && <img src={asset(t.created_by_signature)} alt="" className="h-14 object-contain ml-auto my-2"/>}
          <div className="border-t border-slate-900 inline-block px-8 pt-1 font-semibold">{t.created_by_name || "Authorised Signatory"}</div>
          <div className="text-[9px] text-slate-500 uppercase tracking-widest mt-0.5">Sales Representative</div>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, bold }) {
  return (
    <div className="border-b border-black last:border-b-0 border-r border-black last:border-r-0 p-1.5">
      <div className="text-[9px] uppercase tracking-wider text-slate-600">{label}</div>
      <div className={`text-[12px] ${bold ? "font-bold" : ""}`}>{value || "-"}</div>
    </div>
  );
}

function Row({label, value}){ return <div className="flex justify-between text-slate-700"><span>{label}</span><span className="font-mono">{value}</span></div>; }
function Row2({label, value}){ return <div className="flex justify-between px-4 py-2 border-b border-slate-200 text-slate-700 text-[11px]"><span>{label}</span><span className="font-mono">{value}</span></div>; }
